import React, { useState } from "react";
import { loginUser, registerUser } from "../../services/api";
import { useUser } from "../../context/UserContext";

/**
 * AuthModal — popup for login/register
 * Props:
 *   mode: "login" | "register"
 *   onClose: () => void
 *   onSuccess: () => void   (optional — called after successful auth)
 */
const AuthModal = ({ mode: initialMode = "login", onClose, onSuccess }) => {
  const { login } = useUser();
  const [mode, setMode] = useState(initialMode);

  // Login form state
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showLoginPass, setShowLoginPass] = useState(false);

  // Register form state
  const [regForm, setRegForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showRegPass, setShowRegPass] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError("");
    if (!loginForm.email || !loginForm.password)
      return setError("Email and password are required");
    setLoading(true);
    try {
      const res = await loginUser(loginForm);
      login(res.data.user, res.data.token);
      onClose && onClose();
      onSuccess && onSuccess(res.data.user);
      // If admin, redirect is handled by caller
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Invalid email or password. Please try again.",
      );
      return;
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    if (!regForm.name.trim()) return "Full name is required";
    if (!regForm.email.trim()) return "Email address is required";
    if (!/\S+@\S+\.\S+/.test(regForm.email))
      return "Please enter a valid email address";
    if (regForm.password.length < 6)
      return "Password must be at least 6 characters";
    if (regForm.password !== regForm.confirmPassword)
      return "Passwords do not match";
    return null;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const err = validate();
    if (err) return setError(err);
    setLoading(true);
    try {
      const res = await registerUser({
        name: regForm.name,
        email: regForm.email,
        phone: regForm.phone,
        password: regForm.password,
      });
      login(res.data.user, res.data.token);
      onClose && onClose();
      onSuccess && onSuccess(res.data.user);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={s.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div style={s.modal}>
        {/* Close button */}
        <button style={s.closeBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* Logo */}
        <div style={s.header}>
          <div style={s.logoWrap}>
            <span style={s.logoIcon}>🎓</span>
            <div>
              <div style={s.brand}>SkillYer</div>
              <div style={s.brandSub}>
                Powered by Truhire AI Private Limited
              </div>
            </div>
          </div>
          <h2 style={s.title}>
            {mode === "login" ? "Welcome" : "Create Account"}
          </h2>
          <p style={s.subtitle}>
            {mode === "login"
              ? "Sign in to access courses, jobs & more"
              : "Join SkillYer to explore courses & careers"}
          </p>
        </div>

        {/* Tab switcher */}
        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(mode === "login" ? s.tabActive : {}) }}
            onClick={() => {
              setMode("login");
              setError("");
            }}
          >
            Sign In
          </button>
          <button
            style={{ ...s.tab, ...(mode === "register" ? s.tabActive : {}) }}
            onClick={() => {
              setMode("register");
              setError("");
            }}
          >
            Register
          </button>
        </div>

        {error && <div style={s.errorBox}>⚠️ {error}</div>}

        {/* LOGIN FORM */}
        {mode === "login" && (
          <form onSubmit={handleLoginSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Email Address</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) =>
                  setLoginForm((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="you@example.com"
                style={s.input}
                required
                autoFocus
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <div style={s.passWrap}>
                <input
                  type={showLoginPass ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm((p) => ({ ...p, password: e.target.value }))
                  }
                  placeholder="Enter your password"
                  style={{ ...s.input, paddingRight: 44 }}
                  required
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowLoginPass((v) => !v);
                  }}
                  style={s.eyeBtn}
                >
                  {showLoginPass ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? "⏳ Signing in..." : "🔓 Sign In"}
            </button>
          </form>
        )}

        {/* REGISTER FORM */}
        {mode === "register" && (
          <form onSubmit={handleRegisterSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>
                Full Name <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                value={regForm.name}
                onChange={(e) =>
                  setRegForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="Enter your full name"
                style={s.input}
                required
                autoFocus
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>
                Email Address <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="email"
                value={regForm.email}
                onChange={(e) =>
                  setRegForm((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="you@example.com"
                style={s.input}
                required
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>
                Phone{" "}
                <span
                  style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}
                >
                  (optional)
                </span>
              </label>
              <input
                type="tel"
                value={regForm.phone}
                onChange={(e) =>
                  setRegForm((p) => ({ ...p, phone: e.target.value }))
                }
                placeholder="10-digit mobile number"
                style={s.input}
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>
                Password <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <div style={s.passWrap}>
                <input
                  type={showRegPass ? "text" : "password"}
                  value={regForm.password}
                  onChange={(e) =>
                    setRegForm((p) => ({ ...p, password: e.target.value }))
                  }
                  placeholder="Minimum 6 characters"
                  style={{ ...s.input, paddingRight: 44 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowRegPass((v) => !v)}
                  style={s.eyeBtn}
                >
                  {showRegPass ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>
                Confirm Password <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type={showRegPass ? "text" : "password"}
                value={regForm.confirmPassword}
                onChange={(e) =>
                  setRegForm((p) => ({ ...p, confirmPassword: e.target.value }))
                }
                placeholder="Re-enter your password"
                style={s.input}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? "⏳ Creating account..." : "🚀 Create Account"}
            </button>
          </form>
        )}
      </div>
      <style>{`
        @keyframes authOverlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes authModalIn {
          from { opacity: 0; transform: translateY(32px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
};

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: "16px",
    animation: "authOverlayIn 0.3s ease",
  },
  modal: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 25px 60px rgba(0,0,0,0.35)",
    padding: "36px 32px",
    width: "100%",
    maxWidth: 440,
    position: "relative",
    maxHeight: "90vh",
    overflowY: "auto",
    animation: "authModalIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 16,
    background: "#f3f4f6",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 14,
    color: "#6b7280",
    fontWeight: 700,
    transition: "background 0.15s",
  },
  header: { textAlign: "center", marginBottom: 20 },
  logoWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 14,
  },
  logoIcon: { fontSize: 32 },
  brand: {
    fontSize: 18,
    fontWeight: 800,
    color: "#1e1b4b",
    letterSpacing: "-0.5px",
  },
  brandSub: { fontSize: 11, color: "#9ca3af", fontWeight: 500 },
  title: { fontSize: 20, fontWeight: 700, color: "#1f2937", margin: "0 0 4px" },
  subtitle: { fontSize: 13, color: "#6b7280", margin: 0 },
  tabs: {
    display: "flex",
    background: "#f3f4f6",
    borderRadius: 10,
    padding: 4,
    marginBottom: 20,
    gap: 4,
  },
  tab: {
    flex: 1,
    border: "none",
    borderRadius: 8,
    padding: "9px 0",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    background: "none",
    color: "#6b7280",
    transition: "all 0.15s",
  },
  tabActive: {
    background: "#fff",
    color: "#4f46e5",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#dc2626",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 12,
  },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  input: {
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 13px",
    fontSize: 14,
    color: "#1f2937",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  passWrap: { position: "relative" },
  eyeBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    padding: 0,
  },
  submitBtn: {
    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "13px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 4,
  },
};

export default AuthModal;
